import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/design-system/atoms/modal';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { TextInput } from '@/components/design-system/atoms/text/text-input';
import { createColocationPayment } from '@/lib/coloc-api/admin';
import { showToast } from '@/services/toast-service';
import { PaymentTypeDropdown } from './components/payment-type-dropdown';
import { HouseSelectionDropdown } from './components/house-selection-dropdown';
import { TenantSelectionDropdown } from './components/tenant-selection-dropdown';
import { ServiceProviderDropdown } from './components/service-provider-dropdown';
import {
  paymentTypeOptions,
  isRefundPaymentType,
  isServiceProviderPaymentType,
} from './utils/payment-helpers';
import { usePaymentForm } from './hooks/usePaymentForm';
import { ColocationPaymentCategory } from '../../types/colocation-payment';
import { PaymentFormButtons } from './components/payment-form-buttons';

interface CreatePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreatePaymentModal: React.FC<CreatePaymentModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<any>({});

  const {
    formData,
    setFormData,
    selectedHouse,
    setSelectedHouse,
    selectedTenant,
    setSelectedTenant,
    selectedProvider,
    setSelectedProvider,
    housesQuery,
    setHousesQuery,
    tenants,
    resetForm,
  } = usePaymentForm();

  const createPaymentMutation = useMutation({
    mutationFn: () => {
      const amount = Math.abs(parseFloat(formData.amount));
      const requestBody = {
        payment: {
          amount,
          amount_currency: 'EUR',
          category: formData.paymentType as ColocationPaymentCategory,
          colocation_id: selectedHouse?.id?.toString() || '',
          recipient_id: isServiceProviderPaymentType(formData.paymentType)
            ? selectedProvider?.id?.toString()
            : selectedTenant?.id?.toString(),
          recipient_type: isServiceProviderPaymentType(formData.paymentType)
            ? 'ServiceProvider'
            : 'User',
        },
      };
      return createColocationPayment(requestBody);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getColocationPayments'] });
      showToast.success({ message: t('js.features.admin.transactions.payment_created') });
      resetForm();
      onClose();
    },
    onError: (error: any) => {
      setErrors(error.response?.data?.errors || {});
      const errorMessage =
        error?.response?.data?.errors?.[0] ||
        t('js.features.admin.transactions.payment_creation_error');
      showToast.error({ message: errorMessage });
    },
  });

  const handleSubmit = () => {
    setErrors({});

    if (!formData.amount || !formData.paymentType) {
      showToast.error({
        title: 'Fill required fields',
        text: 'Please fill in all required fields.',
      });
      return;
    }

    if (!selectedHouse) {
      showToast.error({
        title: 'Fill required fields',
        text: 'Please select a house.',
      });
      return;
    }

    if (isRefundPaymentType(formData.paymentType) && !selectedTenant) {
      showToast.error({
        title: 'Fill required fields',
        text: 'Please select a tenant.',
      });
      return;
    }

    if (isServiceProviderPaymentType(formData.paymentType) && !selectedProvider) {
      showToast.error({
        title: 'Fill required fields',
        text: 'Please select a service provider.',
      });
      return;
    }

    const amount = parseFloat(formData.amount);
    if (Number.isNaN(amount) || amount <= 0) {
      showToast.error({
        title: 'Invalid amount',
        text: 'Please enter a valid amount greater than 0.',
      });
      return;
    }

    createPaymentMutation.mutate();
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="w-[650px] overflow-hidden rounded-lg bg-white shadow-medium">
        <div className="flex h-[102px] w-full items-center justify-between bg-R300 px-7 py-3">
          <div className="flex flex-col">
            <span className="text-lead2 font-medium text-text-primary opacity-60">
              Create a new
            </span>
            <span className="text-h4 font-bold text-text-primary">Payment</span>
          </div>
          <IconSaxIcon name="wallet-add" size={70} className="opacity-20" />
        </div>
        <div className="flex w-full overflow-hidden">
          <div className="flex flex-col gap-4 bg-E30 px-4 pt-4">
            <div>
              <span className="text-body font-medium italic text-text-primary opacity-60">
                {t('js.features.admin.transactions.create_payment_modal.instructions.title')}
              </span>
              <div className="flex flex-col gap-3">
                <div className="flex gap-1 pt-[6px] text-body text-text-primary">
                  <span>1.</span>
                  <span>
                    {t(
                      'js.features.admin.transactions.create_payment_modal.instructions.steps.step1'
                    )}
                  </span>
                </div>
                <div className="flex gap-1 text-body text-text-primary">
                  <span>2.</span>
                  <span>
                    {t(
                      'js.features.admin.transactions.create_payment_modal.instructions.steps.step2'
                    )}
                  </span>
                </div>
                <div className="flex gap-1 text-body text-text-primary">
                  <span>3.</span>
                  <span>
                    {t(
                      'js.features.admin.transactions.create_payment_modal.instructions.steps.step3'
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 bg-white p-7">
            <div className="flex w-[320px] flex-col gap-4">
              <TextInput
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ amount: e.target.value })}
                placeholder="Enter amount"
                invalid={!!errors.amount}
                errorMessage={errors.amount}
                required
              />
              <PaymentTypeDropdown
                value={formData.paymentType}
                onChange={(value) => setFormData({ paymentType: value })}
                options={paymentTypeOptions}
              />
              {formData.paymentType && (
                <HouseSelectionDropdown
                  selectedHouse={selectedHouse}
                  setSelectedHouse={setSelectedHouse}
                  housesQuery={housesQuery}
                  setHousesQuery={setHousesQuery}
                />
              )}
              {isRefundPaymentType(formData.paymentType) && selectedHouse && (
                <TenantSelectionDropdown
                  selectedTenant={selectedTenant}
                  setSelectedTenant={setSelectedTenant}
                  tenants={tenants}
                  disabled={!selectedHouse}
                />
              )}
              {isServiceProviderPaymentType(formData.paymentType) && selectedHouse && (
                <ServiceProviderDropdown
                  selectedProvider={selectedProvider}
                  setSelectedProvider={setSelectedProvider}
                />
              )}
            </div>
            <div className="mt-8">
              <PaymentFormButtons
                isLoading={createPaymentMutation.isLoading}
                onClose={onClose}
                amount={formData.amount}
                paymentType={formData.paymentType}
                onSubmit={handleSubmit}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
